import React, { useEffect, useState, createContext, useContext } from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { Flex } from 'reflexbox/styled-components'
import { darken } from 'polished'

import theme from 'theme'
import media from 'media'

import IconClose from '@anvilco-pkg/ui/components/Icon/IconClose'

import UniversalLink from 'components/UniversalLink'

const LinkStyledContainer = styled.span`
  text-decoration: underline;
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.green};
  margin-left: 6px;
`

export const BannerContainer = styled(Flex)
  .attrs({
    alignItems: 'center',
    justifyContent: 'center',
  })
  .withConfig({
    shouldForwardProp: (prop) => ['isOpen', 'background'].indexOf(prop) === -1,
  })`
  position: relative;
  width: 100%;
  color: ${({ background }) => (background ? theme.colors.black : theme.colors.white)};
  font-size: ${theme.fontSizes[50]}px;
  background: ${({ background }) => background || theme.colors.blacks[70]};
  margin: 0 auto;
  text-align: center;
  transition: all 0.3s linear;

  ${({ isOpen }) =>
    isOpen
      ? css`
          transform: translateY(0%);
          height: auto;
          padding: 16px 36px;
        `
      : css`
          transform: translateY(-100%);
          height: 0;
          padding: 0;
        `}

  ${LinkStyledContainer} {
    color: ${({ background }) => (background ? theme.colors.black : theme.colors.green)};
  }

  &:hover {
    background: ${({ background }) => (background ? darken(0.15, background) : theme.colors.black)};
    color: ${({ background }) => (background ? theme.colors.black : theme.colors.white)};  
    text-decoration: none;

    ${LinkStyledContainer} {
      color: ${({ background }) => (background ? theme.colors.black : theme.colors.hoverGreen)} !important;
    }
  }
`

const IconContainer = styled.div`
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
`

const StyledIconClose = styled(IconClose).withConfig({
  shouldForwardProp: (prop) => ['background'].indexOf(prop) === -1,
})`
  g,
  path,
  rect {
    fill: ${theme.colors.blacks[30]};
  }

  :hover {
    g,
    path,
    rect {
      fill: ${({ background }) =>
        background ? theme.colors.black : theme.colors.red};
    }
  }
`

const BannerMessage = styled.div`
  margin-right: 30px;
`

const IconWrapper = styled.div`
  margin-right: 16px;
  display: none;

  ${media.desktop`
    display: block;
    width: 27px;
    height: 27px;
  `}

  > * {
    width: 100%;
    height: auto;
  }
`

const Banner = ({
  text,
  linkText,
  linkTo,
  isOpen,
  background,
  boldText,
  icon,
  onClose,
  ...others
}) => {
  return (
    <BannerContainer
      isOpen={isOpen}
      to={linkTo}
      as={UniversalLink}
      background={background}
      {...others}
    >
      <IconWrapper>{icon}</IconWrapper>
      <BannerMessage>
        {boldText && <strong style={{ marginRight: '6px' }}>{boldText}</strong>}
        <span>{text}</span>
        <LinkStyledContainer>{linkText}</LinkStyledContainer>
        {!linkText.endsWith('.') ? '.' : ''}
      </BannerMessage>
      <IconContainer
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onClose()
        }}
      >
        <StyledIconClose background={background} />
      </IconContainer>
    </BannerContainer>
  )
}

export default Banner

Banner.propTypes = {
  text: PropTypes.string,
  linkText: PropTypes.string,
  linkTo: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  boldText: PropTypes.string,
  background: PropTypes.string,
  icon: PropTypes.node,
}

export const BannerContext = createContext(null)

export const useBannerHeight = () => {
  const [height, setHeight] = useState(null)

  const { bannerIsOpen } = useContext(BannerContext) || {}

  useEffect(() => {
    const banner = document.querySelector(`${BannerContainer}`)
    if (banner && bannerIsOpen) {
      setHeight(banner.clientHeight)
    } else {
      setHeight(0)
    }
  }, [bannerIsOpen])

  return height
}
